import PropTypes from 'prop-types';
import React, { memo } from 'react';

import { FlexView, Row, Col } from '@cuemath/comps';
import { Grid } from 'semantic-ui-react';

import { FieldContainer, LinkContainer } from './utils/comps';
import {
  ApplicantProfileHeading,
  ApplicantProfileInfoWrapper,
} from './utils/styled-components';

export const FIELD_TYPE = {
  FIELD: 'field',
  LINK: 'link',
};

const APPLICANT_DETAILS_LABELS = {
  ID: 'Applicant ID',
  HEADING: 'Appplicant Details',
  FIRST_NAME: 'First Name',
  LAST_NAME: 'Last Name',
  STATE: 'State',
  COUNTRY_CODE: 'Country Code',
  PHONE: 'Phone',
  EMAIL: 'Email',
};

const APPLICANT_EXTRA_DETAIL = {
  HEADING: 'Extra Data',
  HIGHTEST_DEGREE: 'Highest Degree',
  MATHS_EXPERIENCE: 'Maths Experience',
  WATCHED_VIDEO: 'Watched Video',
  STREAM: 'Stream',
  GRADE_GROUP: 'Grade group',
  MATH_TEST_LINK: 'Math Test Link',
  MATH_TEST_STATUS: 'Math Test Status',
  SOP_TEST_STATUS: 'SOP Test Status',
};

const CET_TEST_TYPE = {
  MATH_TEST: 'MATH_TEST',
  SOP: 'SOP',
};

const NOT_SUBMITTED = 'Not Submitted';

const ApplicantProfile = ({ applicantDetail }) => {
  const renderData = (heading = '', fields = [], type = '', renderer = () => {}) => {
    return (
      <FlexView>
        <ApplicantProfileHeading>{heading}</ApplicantProfileHeading>
        <ApplicantProfileInfoWrapper>
          <Grid>{renderer(fields, type)}</Grid>
        </ApplicantProfileInfoWrapper>
      </FlexView>
    );
  };

  const renderColumnContainersInGrid = (fields = [], type) => {
    return fields.map((row = [], index) => (
      <Grid.Row key={index}>
        {row.map(({ label, value, width }) => {
          return (
            <React.Fragment key={label}>
              {type === FIELD_TYPE.FIELD && <>{FieldContainer(label, value, width)}</>}
              {type === FIELD_TYPE.LINK && <>{LinkContainer(label, value, width)}</>}
            </React.Fragment>
          );
        })}
      </Grid.Row>
    ));
  };

  const renderApplicantProfileDetail = applicantProfileInfo => {
    const {
      id,
      first_name: firstName,
      last_name: lastName,
      phone,
      country_code: countryCode,
      email,
      state,
    } = applicantProfileInfo;

    const { FIRST_NAME, LAST_NAME, ID, STATE, HEADING, COUNTRY_CODE, PHONE, EMAIL } =
      APPLICANT_DETAILS_LABELS || {};

    const fieldsData = [
      [
        {
          label: ID,
          value: id,
          width: 8,
        },
      ],
      [
        {
          label: FIRST_NAME,
          value: firstName,
          width: 8,
        },
        {
          label: LAST_NAME,
          value: lastName,
          width: 8,
        },
      ],
      [
        {
          label: COUNTRY_CODE,
          value: countryCode,
          width: 8,
        },
        { label: PHONE, value: phone, width: 8 },
      ],
      [
        { label: STATE, value: state, width: 8 },
        { label: EMAIL, value: email, width: 8 },
      ],
    ];

    return renderData(
      HEADING,
      fieldsData,
      FIELD_TYPE.FIELD,
      renderColumnContainersInGrid,
    );
  };

  const renderApplicantProfileExtraDetail = applicantProfileInfo => {
    const {
      highest_degree: highestDegree,
      stream,
      maths_experience: MathsExperience,
      watched_video: watchedVideo,
      grade: gradeGroup,
    } = applicantProfileInfo?.profile_attributes || {};

    const { cet_details: cetDetails = {} } = applicantProfileInfo || {};

    const {
      HIGHTEST_DEGREE,
      MATHS_EXPERIENCE,
      STREAM,
      HEADING,
      WATCHED_VIDEO,
      GRADE_GROUP,
      MATH_TEST_LINK,
      MATH_TEST_STATUS,
      SOP_TEST_STATUS,
    } = APPLICANT_EXTRA_DETAIL || {};

    const { MATH_TEST, SOP } = CET_TEST_TYPE;

    const fieldsData = [
      [
        {
          label: HIGHTEST_DEGREE,
          value: highestDegree ? highestDegree : NOT_SUBMITTED,
          width: 8,
        },
        {
          label: STREAM,
          value: stream ? stream : NOT_SUBMITTED,
          width: 8,
        },
      ],
      [
        {
          label: MATHS_EXPERIENCE,
          value: MathsExperience ? MathsExperience : NOT_SUBMITTED,
          width: 8,
        },
        {
          label: WATCHED_VIDEO,
          value: watchedVideo ? String(watchedVideo) : NOT_SUBMITTED,
          width: 8,
        },
        {
          label: GRADE_GROUP,
          value: gradeGroup ? gradeGroup : NOT_SUBMITTED,
          width: 8,
        },
      ],
    ];

    if (cetDetails?.[MATH_TEST]) {
      const { test_link: mathTestLink, status: mathTestStatus } = cetDetails[MATH_TEST];

      fieldsData.push([
        {
          label: MATH_TEST_STATUS,
          value: mathTestStatus,
          width: 8,
        },
        {
          label: MATH_TEST_LINK,
          value: mathTestLink,
          width: 8,
        },
      ]);
    }

    if (cetDetails?.[SOP]) {
      const { status: sopTestStatus } = cetDetails[SOP];

      fieldsData.push([
        {
          label: SOP_TEST_STATUS,
          value: sopTestStatus,
          width: 8,
        },
      ]);
    }

    return renderData(
      HEADING,
      fieldsData,
      FIELD_TYPE.FIELD,
      renderColumnContainersInGrid,
    );
  };

  return (
    <Row>
      <Col auto>{renderApplicantProfileDetail(applicantDetail)}</Col>
      <Col auto>{renderApplicantProfileExtraDetail(applicantDetail)}</Col>
    </Row>
  );
};

ApplicantProfile.propTypes = {
  applicantDetail: PropTypes.shape({
    id: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    phone: PropTypes.string,
    country_code: PropTypes.string,
    email: PropTypes.string,
    state: PropTypes.string,
    profile_attributes: PropTypes.shape({}),
  }).isRequired,
};

ApplicantProfile.defaultProps = {
  state: '',
};

export default memo(ApplicantProfile);
